/**
 * Baccarat-specific event types and interfaces.
 * Same six event names as roulette (unified suspension logic) with
 * baccarat's payloads — see docs/features/baccarat-event-contract.md.
 */

export namespace BaccaratEvents {
  /**
   * Event types for baccarat.
   * Same names as blackjack/roulette for unified suspension logic.
   */
  export enum Type {
    WELCOME = 'welcome',
    PLACE_BET = 'place_bet',
    BETS_UPDATE = 'bets_update',
    BET_CONFIRMATION = 'bet_confirmation',
    GAME_OVER = 'game_over',
    BET_TIMER_EXPIRED = 'bet_timer_expired',
  }

  /**
   * Baccarat bet types. The engine's `house` is renamed to `banker` at the
   * client boundary, so only this vocabulary reaches the agent.
   * Comments encode the usual gross multipliers; the table's `payTable` is
   * configurable, so nothing here is validated against them.
   */
  export type BetType =
    | 'player' // Player side — gross 2×
    | 'banker' // Banker side — gross 1.95× (5% commission)
    | 'tie' // Tie — gross 9×
    | 'player_pair' // Player's opening two cards share a rank — gross 12×
    | 'banker_pair' // Banker's opening two cards share a rank — gross 12×
    | 'either_pair' // Either side shows a pair — gross 6×
    | 'perfect_pair'; // A pair of the same suit on either side — gross 26×

  /** Round result: the side with the higher total, or a tie. */
  export type Result = 'player' | 'banker' | 'tie';

  /**
   * Per-bet settlement. Three-valued because a tie returns the player/banker
   * stakes and a `won` boolean cannot express that.
   */
  export type Outcome = 'won' | 'lost' | 'push';

  /**
   * A single bet placed by the player
   */
  export interface Bet {
    type: BetType;
    amount: number; // > 0
  }

  /**
   * Bet with its settlement (used in game_over).
   * `payout` is the GROSS return (stake + profit): 0 on a loss, the stake
   * back on a push.
   */
  export interface BetOutcome extends Bet {
    outcome: Outcome;
    payout: number;
  }

  /**
   * One side's cards and the provider's mod-10 total. Baccarat hands are
   * always face-up, so there is no "cover" sentinel here.
   */
  export interface Hand {
    cards: string[]; // 2-3 cards, each "RANK-SUIT" (A,2..10,J,Q,K × H,D,C,S)
    total: number; // 0-9
  }

  /**
   * Base interface for all baccarat events
   */
  interface BaseEvent {
    type: Type;
  }

  /**
   * Welcome event when player joins. Carries no payload.
   */
  export interface Welcome extends BaseEvent {
    type: Type.WELCOME;
    data?: undefined;
  }

  /**
   * Betting window opened. Carries no payload.
   */
  export interface PlaceBet extends BaseEvent {
    type: Type.PLACE_BET;
    data?: undefined;
  }

  /**
   * Silent live-table snapshot during the betting window. Full snapshot,
   * last write wins — never a diff. Empty `bets` is a valid state (player
   * cleared the table). No roundId: the round does not exist until the deal
   * response. Carries no money meaning — bet_confirmation is the source of
   * truth.
   */
  export interface BetsUpdate extends BaseEvent {
    type: Type.BETS_UPDATE;
    data: {
      bets: Bet[];
      totalBet: number;
    };
  }

  /**
   * Bet confirmation — the felt the client sent to LEGA, plus the round id
   * that came back.
   */
  export interface BetConfirmation extends BaseEvent {
    type: Type.BET_CONFIRMATION;
    data: {
      bets: Bet[];
      totalBet: number;
      roundId: string; // LEGA playId
    };
  }

  /**
   * Game over — both hands, the result, and every confirmed bet settled.
   */
  export interface GameOver extends BaseEvent {
    type: Type.GAME_OVER;
    data: {
      playerHand: Hand;
      bankerHand: Hand;
      result: Result;
      bets: BetOutcome[];
      totalBet: number;
      totalWin: number; // gross engine creditsWon
      roundId: string;
    };
  }

  /**
   * Bet timer expired event - triggers AI suspension
   */
  export interface BetTimerExpired extends BaseEvent {
    type: Type.BET_TIMER_EXPIRED;
    data?: undefined;
  }

  /**
   * Union type of all baccarat events
   */
  export type Event = Welcome | PlaceBet | BetsUpdate | BetConfirmation | GameOver | BetTimerExpired;

  /**
   * Helper functions to create baccarat events
   */
  export function create<T extends Event>(type: T['type'], data?: T['data']): T {
    return {
      type,
      data,
    } as T;
  }

  export function createWelcome(): Welcome {
    return create(Type.WELCOME, undefined);
  }

  export function createPlaceBet(): PlaceBet {
    return create(Type.PLACE_BET, undefined);
  }

  export function createBetsUpdate(bets: Bet[], totalBet: number): BetsUpdate {
    return create(Type.BETS_UPDATE, { bets, totalBet });
  }

  export function createBetConfirmation(
    bets: Bet[],
    totalBet: number,
    roundId: string
  ): BetConfirmation {
    return create(Type.BET_CONFIRMATION, { bets, totalBet, roundId });
  }

  export function createGameOver(
    playerHand: Hand,
    bankerHand: Hand,
    result: Result,
    bets: BetOutcome[],
    totalBet: number,
    totalWin: number,
    roundId: string
  ): GameOver {
    return create(Type.GAME_OVER, {
      playerHand,
      bankerHand,
      result,
      bets,
      totalBet,
      totalWin,
      roundId,
    });
  }

  export function createBetTimerExpired(): BetTimerExpired {
    return create(Type.BET_TIMER_EXPIRED, undefined);
  }

  /**
   * Type guards
   */
  export function isWelcome(event: Event): event is Welcome {
    return event.type === Type.WELCOME;
  }

  export function isPlaceBet(event: Event): event is PlaceBet {
    return event.type === Type.PLACE_BET;
  }

  export function isBetsUpdate(event: Event): event is BetsUpdate {
    return event.type === Type.BETS_UPDATE;
  }

  export function isBetConfirmation(event: Event): event is BetConfirmation {
    return event.type === Type.BET_CONFIRMATION;
  }

  export function isGameOver(event: Event): event is GameOver {
    return event.type === Type.GAME_OVER;
  }

  export function isBetTimerExpired(event: Event): event is BetTimerExpired {
    return event.type === Type.BET_TIMER_EXPIRED;
  }

  /** Canonical runtime-enumerable values used by validators. */
  export const BET_TYPES: readonly BetType[] = [
    'player',
    'banker',
    'tie',
    'player_pair',
    'banker_pair',
    'either_pair',
    'perfect_pair',
  ] as const;
  export const RESULTS: readonly Result[] = ['player', 'banker', 'tie'] as const;
  export const OUTCOMES: readonly Outcome[] = ['won', 'lost', 'push'] as const;

  /** The two sides a tie pushes; every other bet type is decided outright. */
  export const MAIN_BET_TYPES: readonly BetType[] = ['player', 'banker'] as const;
}
